import { Link, useNavigate } from "@/components/LocalizedLink";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Menu, X, Wine, LogOut } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useAndroidAppMode } from "@/hooks/useAndroidAppMode";

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isAndroidApp = useAndroidAppMode();

  const nav = [
    { to: "/practice", label: t("aHeader.nav.practice") },
    { to: "/exam", label: t("aHeader.nav.exam") },
    ...(isAndroidApp ? [] : [{ to: "/pricing", label: t("aHeader.nav.pricing") }]),
    ...(isAndroidApp ? [] : [{ to: "/foretag", label: t("aHeader.nav.business", { defaultValue: "Fler licenser" }) }]),
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const logout = async () => {
    await supabase.auth.signOut();
    setOpen(false);
    navigate({ to: "/" });
  };

  return (
    <header
      className={`sticky top-0 z-40 transition-all ${
        scrolled ? "bg-background/85 backdrop-blur border-b border-border" : "bg-transparent"
      }`}
    >
      <div className="container-page flex h-16 items-center justify-between">
        <Link to="/" className="flex items-center gap-2 font-display font-semibold text-foreground">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <Wine className="h-4 w-4" />
          </span>
          <span className="text-lg tracking-tight">{t("aHeader.brand")}</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {nav.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              activeProps={{ className: "px-3 py-2 text-sm text-foreground font-medium" }}
            >
              {n.label}
            </Link>
          ))}
          <div className="ml-2">
            <LanguageSwitcher />
          </div>
          {!loading && (user ? (
            <>
              <Link to="/dashboard" className="ml-2 px-3 py-2 text-sm text-foreground hover:text-accent">{t("aHeader.nav.myPage")}</Link>
              <button onClick={logout} className="ml-1 inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm hover:bg-secondary">
                <LogOut className="h-3.5 w-3.5" /> {t("aHeader.nav.logout")}
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="ml-2 px-3 py-2 text-sm text-foreground hover:text-accent transition-colors">{t("aHeader.nav.login")}</Link>
              <Link to="/signup" className="ml-1 inline-flex items-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors">{t("aHeader.nav.signup")}</Link>
            </>
          ))}
        </nav>

        <div className="md:hidden flex items-center gap-1">
          <LanguageSwitcher compact />
          <button
            aria-label={t("aHeader.nav.menu")}
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md hover:bg-muted"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-border bg-background">
          <div className="container-page py-3 flex flex-col">
            {nav.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="py-3 text-base text-foreground">
                {n.label}
              </Link>
            ))}
            <div className="mt-2 flex gap-2">
              {user ? (
                <>
                  <Link to="/dashboard" onClick={() => setOpen(false)} className="flex-1 rounded-md border border-border px-4 py-2 text-center text-sm">{t("aHeader.nav.myPage")}</Link>
                  <button onClick={logout} className="flex-1 rounded-md bg-primary px-4 py-2 text-center text-sm text-primary-foreground">{t("aHeader.nav.logout")}</button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="flex-1 rounded-md border border-border px-4 py-2 text-center text-sm">{t("aHeader.nav.login")}</Link>
                  <Link to="/signup" onClick={() => setOpen(false)} className="flex-1 rounded-md bg-primary px-4 py-2 text-center text-sm text-primary-foreground">{t("aHeader.nav.signup")}</Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
